// https://raw.githubusercontent.com/morganherlocker/cubic-spline/85c436045d08c37c59887b3932bb190990f4b1f6/index.ts
// MIT

class Spline {
  readonly xs: number[];
  readonly ys: number[];
  readonly ks: Float64Array;

  constructor(xs: number[], ys: number[]) {
    this.xs = xs;
    this.ys = ys;
    this.ks = this.getNaturalKs(new Float64Array(this.xs.length));
  }

  getNaturalKs(ks: Float64Array): Float64Array {
    const n = this.xs.length - 1;
    const A = zerosMat(n + 1, n + 2);

    // rows
    for (let i = 1; i < n; i++) {
      const a = this.xs[i] - this.xs[i - 1];
      const r = this.xs[i + 1] - this.xs[i];
      const l = this.ys[i + 1] - this.ys[i];

      let c = 1 / r;
      if (r === 0) {
        c = 0;
      }

      let z = l / (r * r);

      if (r === 0) {
        z = 0;
      }

      A[i][i - 1] = 1 / a;
      A[i][i] = 2 * (1 / a + c);
      A[i][i + 1] = c;
      A[i][n + 1] = 3 * (a / (a * a) + z);
    }

    const a = this.xs[1] - this.xs[0];

    A[0][0] = 2 / a;
    A[0][1] = 1 / a;
    A[0][n + 1] = (3 * a) / (a * a);

    let l = this.xs[n] - this.xs[n - 1];

    if (l <= 0) {
      A[n][n - 1] = 0;
      A[n][n] = 0;
      A[n][n + 1] = 0;
    } else {
      A[n][n - 1] = 1 / l;
      A[n][n] = 2 / l;
      A[n][n + 1] = (3 * l) / (l * l);
    }

    return solve(A, ks);
  }

  /**
   * inspired by https://stackoverflow.com/a/40850313/4417327
   */
  getIndexBefore(target: number): number {
    let low = 0;
    let high = this.xs.length;
    let mid = 0;
    while (low < high) {
      mid = Math.floor((low + high) / 2);
      if (this.xs[mid] < target && mid !== low) {
        low = mid;
      } else if (this.xs[mid] >= target && mid !== high) {
        high = mid;
      } else {
        high = low;
      }
    }

    if (low === this.xs.length - 1) {
      return this.xs.length - 1;
    }

    return low + 1;
  }

  at(x: number): number {
    let i = this.getIndexBefore(x);
    const t = (x - this.xs[i - 1]) / (this.xs[i] - this.xs[i - 1]);
    const a =
      this.ks[i - 1] * (this.xs[i] - this.xs[i - 1]) -
      (this.ys[i] - this.ys[i - 1]);
    const b =
      -this.ks[i] * (this.xs[i] - this.xs[i - 1]) +
      (this.ys[i] - this.ys[i - 1]);
    const q =
      (1 - t) * this.ys[i - 1] +
      t * this.ys[i] +
      t * (1 - t) * (a * (1 - t) + b * t);
    return q;
  }
}

function solve(A: Float64Array[], ks: Float64Array): Float64Array {
  const m = A.length;
  let h = 0;
  let k = 0;
  while (h < m && k <= m) {
    let i_max = 0;
    let max = -Infinity;
    for (let i = h; i < m; i++) {
      const v = Math.abs(A[i][k]);
      if (v > max) {
        i_max = i;
        max = v;
      }
    }

    if (A[i_max][k] === 0) {
      k++;
    } else {
      swapRows(A, h, i_max);
      for (let i = h + 1; i < m; i++) {
        const f = A[i][k] / A[h][k];
        A[i][k] = 0;
        for (let j = k + 1; j <= m; j++) A[i][j] -= A[h][j] * f;
      }
      h++;
      k++;
    }
  }

  for (
    let i = m - 1;
    i >= 0;
    i-- // rows = columns
  ) {
    let v = 0;
    if (A[i][i]) {
      v = A[i][m] / A[i][i];
    }
    ks[i] = v;
    for (
      let j = i - 1;
      j >= 0;
      j-- // rows
    ) {
      A[j][m] -= A[j][i] * v;
      A[j][i] = 0;
    }
  }
  return ks;
}

function zerosMat(r: number, c: number): Float64Array[] {
  const A = [];
  for (let i = 0; i < r; i++) A.push(new Float64Array(c));
  return A;
}

function swapRows(m: Float64Array[], k: number, l: number): void {
  let p = m[k];
  m[k] = m[l];
  m[l] = p;
}

export default Spline;
